import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SettingsIcon from "@mui/icons-material/Settings";
import type { PartMatch } from "../services/PartService";

export interface PartMatchCardProps {
  match: PartMatch;
  onClick: (partId: number) => void;
  /**
   * If provided, shows a "Compare" button that fires `onCompare(matchPartId)`
   * with the matched part's ID. The caller (e.g. PartDetailView) supplies the
   * current part's ID — this card only knows about the match.
   */
  onCompare?: (matchPartId: number) => void;
  /** Disable the Compare button while a comparison request is in flight. */
  isCompareLoading?: boolean;
  /**
   * The match part ID currently being compared, if any. When this matches
   * the card's own match ID, the Compare button switches to a "starting"
   * label so the user sees which card they kicked off.
   */
  comparingPartId?: number | null;
  /**
   * Reserve a left-edge photo slot on the card. Shows the same gear-icon
   * placeholder ``PartCard`` uses on the dashboard until real photo
   * fetching is wired up.
   */
  showPhoto?: boolean;
}

const calculateSimilarity = (distance: number | null): number | null => {
  if (distance == null) {
    return null;
  }
  // Convert cosine distance to similarity percentage and clamp for display.
  return Math.max(0, Math.min(100, Math.round((1 - distance) * 100)));
};

const getColorFromSimilarity = (
  similarity: number | null,
): "success" | "warning" | "error" | "default" => {
  if (similarity == null) {
    return "default";
  } else if (similarity >= 90) {
    return "success";
  } else if (similarity >= 75) {
    return "warning";
  } else if (similarity >= 0) {
    return "error";
  } else {
    // Handle unexpected negative similarity gracefully.
    return "default";
  }
};

const PartMatchCard = ({
  match,
  onClick,
  onCompare,
  isCompareLoading,
  comparingPartId,
  showPhoto = false,
}: PartMatchCardProps) => {
  const isThisCardComparing = comparingPartId === match.part_id;
  // Convert cosine distance to similarity percentage and clamp for display.
  const similarity = calculateSimilarity(match.distance);

  return (
    <Card
      variant="outlined"
      sx={{ "&:hover": { borderColor: "primary.main" } }}
    >
      <CardActionArea
        onClick={() => onClick(match.part_id)}
        sx={{
          "& .MuiCardActionArea-focusHighlight": { opacity: 0 },
          "&:hover .MuiCardActionArea-focusHighlight": { opacity: 0 },
          // When the photo slot is on, lay it out side-by-side.
          ...(showPhoto && {
            display: "flex",
            alignItems: "stretch",
          }),
        }}
      >
        {showPhoto && (
          <Box
            sx={{
              width: 72,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: grey[100],
            }}
          >
            <SettingsIcon sx={{ fontSize: 32, color: grey[400] }} />
          </Box>
        )}
        <CardContent
          sx={{
            pb: onCompare ? 1 : undefined,
            // `minWidth: 0` lets the flex child honor noWrap on the name
            // typography without overflowing the card.
            ...(showPhoto && { flex: 1, minWidth: 0 }),
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              noWrap
              sx={{ flex: 1 }}
            >
              {match.name}
            </Typography>
            {similarity != null && (
              <Chip
                label={`${similarity}%`}
                size="small"
                color={getColorFromSimilarity(similarity)}
                sx={{ ml: 1 }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            {match.part_type && (
              <Chip label={match.part_type} size="small" variant="outlined" />
            )}
            {match.manufacturer && (
              <Chip
                label={match.manufacturer}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {match.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
              noWrap
            >
              {match.description}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>

      {onCompare && (
        <Box
          sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<CompareArrowsIcon />}
            disabled={isCompareLoading}
            onClick={(e) => {
              e.stopPropagation();
              onCompare(match.part_id);
            }}
          >
            {isThisCardComparing ? "Starting comparison..." : "Compare"}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default PartMatchCard;
